/**
 * @name add
 * @category Common Helpers
 * @summary Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
 *
 * @description
 * Add the specified years, months, weeks, days, hours, minutes and seconds to the given date.
 *
 * @param date - the date to be changed
 * @param duration - the object with years, months, weeks, days, hours, minutes and seconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 *
 * | Key            | Description                        |
 * |----------------|------------------------------------|
 * | years          | Amount of years to be added        |
 * | months         | Amount of months to be added       |
 * | weeks          | Amount of weeks to be added        |
 * | days           | Amount of days to be added         |
 * | hours          | Amount of hours to be added        |
 * | minutes        | Amount of minutes to be added      |
 * | seconds        | Amount of seconds to be added      |
 *
 * All values default to 0
 *
 * @returns the new date with the seconds added
 *
 * @example
 * // Add the following duration to 1 September 2014, 10:19:50
 * const result = add(new Date(2014, 8, 1, 10, 19, 50), {
 *   years: 2,
 *   months: 9,
 *   weeks: 1,
 *   days: 7,
 *   hours: 5,
 *   minutes: 9,
 *   seconds: 30,
 * })
 * //=> Thu Jun 15 2017 15:29:20
 */

import * as addMonths from 'date-fns/add_months';
import * as addDays from 'date-fns/add_days';
import toDate from './toDate';
import constructFrom from './constructFrom';

export interface Duration {
    years?: number;
    months?: number;
    weeks?: number;
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
}

export default function add<DateType extends Date>(
    dirtyDate: DateType | number,
    duration: Duration
): DateType {
    const {
        years = 0,
        months = 0,
        weeks = 0,
        days = 0,
        hours = 0,
        minutes = 0,
        seconds = 0,
    } = duration;

    // Add years and months
    const date = toDate(dirtyDate);
    const dateWithMonths =
        months || years ? addMonths(date, months + years * 12) : date;

    // Add weeks and days
    const dateWithDays =
        days || weeks
            ? addDays(dateWithMonths, days + weeks * 7)
            : dateWithMonths;

    // Add days, hours, minutes and seconds
    const minutesToAdd = minutes + hours * 60;
    const secondsToAdd = seconds + minutesToAdd * 60;
    const msToAdd = secondsToAdd * 1000;
    const finalDate = constructFrom(
        dirtyDate,
        dateWithDays.getTime() + msToAdd
    );

    return finalDate;
}



// WEBPACK FOOTER //
// ./src/utils/date/add.ts